import { FC, useContext } from 'react'
import { useSnapshot } from 'valtio'
import { MuxVideoPlayerWithTrim } from './MuxVideoPlayerWithTrim'
import { FeatureFlagContext } from '../FeatureFlagProvider/FeatureFlagProvider'
import { FeatureFlagName } from '@app/services/featureFlags/types'
import { CurrentQtiPackageState } from '@app/storage'

export interface MuxVideoPlayerProps {
  domNode: any
  className?: string
}

/**
 * MuxVideoPlayer component that extracts video data from QTI XML domNode
 * and passes it to MuxVideoPlayerWithTrim for rendering
 */
export const MuxVideoPlayer: FC<MuxVideoPlayerProps> = ({ domNode, className }) => {
  const { featureFlags } = useContext(FeatureFlagContext)
  const { tests } = useSnapshot(CurrentQtiPackageState)

  // Extract playback ID from <source> tag
  const sourceNode = domNode.children?.find((child: any) => child.name === 'source')
  const srcUrl = sourceNode?.attribs?.src || ''
  const playbackId = srcUrl.split('/').find((part: string) => part.includes('.m3u8'))?.replace('.m3u8', '') || ''

  // Extract trim data from data attributes
  const startTime = domNode.attribs?.['data-starttime'] ? parseFloat(domNode.attribs['data-starttime']) : undefined
  const endTime = domNode.attribs?.['data-endtime'] ? parseFloat(domNode.attribs['data-endtime']) : undefined

  // Extract autoplay
  const autoPlay = domNode.attribs?.autoplay === 'autoplay' || domNode.attribs?.autoplay === 'true'

  // Extract showCaptions - when true, captions are shown and CC button is visible
  // HTML boolean attributes may come as: "true", true, "", or the attribute name itself (e.g. "data-showcaptions")
  const showCaptionsAttr = domNode.attribs?.['data-showcaptions']
  const showCaptions = showCaptionsAttr !== undefined && showCaptionsAttr !== 'false' && showCaptionsAttr !== false

  // Extract subtitle track ID from <track> element for transcript download
  const trackNode = domNode.children?.find((child: any) => child.name === 'track')
  const subtitleTrackId = trackNode?.attribs?.['data-subtitleid']

  // Extract video title from data attribute, falling back to the test/lesson name
  const videoTitle = (domNode.attribs?.['data-videotitle'] || tests?.[0]?.title) ?? ''

  return (
    <MuxVideoPlayerWithTrim
      playbackId={playbackId}
      startTime={startTime}
      endTime={endTime}
      extraSourceParams={endTime ? { assetEndTime: Math.round(endTime * 10) / 10 } : undefined}
      thumbnailTime={startTime}
      autoPlay={autoPlay}
      showCaptions={showCaptions 
        && featureFlags[FeatureFlagName.video_use_mux_captions_students]
      }
      subtitleTrackId={subtitleTrackId}
      videoTitle={videoTitle}
      className={className}
    />
  )
}

MuxVideoPlayer.displayName = 'MuxVideoPlayer'
